import { itemList } from '@forge42/seo-tools/structured-data/item-list'
import { organization } from '@forge42/seo-tools/structured-data/organization'

/**
 * Check if a string is a valid absolute URL
 */
function isAbsoluteUrl(url: string): boolean {
	try {
		new URL(url)
		return true
	} catch {
		return false
	}
}

/**
 * Build Organization JSON-LD for RentDFW
 */
export function buildOrganizationLd({ origin }: { origin: string }): string {
	return JSON.stringify(
		organization({
			'@type': 'Organization',
			name: 'RentDFW',
			url: origin,
			logo: `${origin}/img/logo.png`,
			sameAs: [
				// Add social media profiles here when available
			],
		}),
	)
}

/**
 * Build Breadcrumb JSON-LD (manual implementation to avoid strict URL validation)
 */
export function buildBreadcrumbLd(
	items: Array<{ name: string; url: string }>,
): string {
	const itemListElement = items
		.filter((i) => isAbsoluteUrl(i.url))
		.map((item, index) => ({
			'@type': 'ListItem',
			position: index + 1,
			name: item.name,
			item: item.url,
		}))

	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'BreadcrumbList',
		itemListElement,
	})
}

/**
 * Build ItemList JSON-LD
 */
export function buildItemListLd({
	name,
	items,
}: {
	name: string
	items: Array<{ name: string; url: string }>
}): string {
	return JSON.stringify(
		itemList({
			'@type': 'ItemList',
			name,
			itemListElement: items.map((item, index) => ({
				'@type': 'ListItem' as const,
				position: index + 1,
				name: item.name,
				url: item.url,
			})),
		} as any),
	)
}

/**
 * Build LocalBusiness JSON-LD for business detail pages
 */
export function buildLocalBusinessLd(
	business: {
		name: string
		description?: string | null
		aiDescription?: string | null
		address?: string | null
		phone?: string | null
		website?: string | null
		featuredMedia?: { url: string | null } | null
		cities: Array<{ name: string }>
		categories: Array<{ name: string }>
		subcategories: Array<{ name: string }>
	},
	canonicalUrl: string,
) {
	const schema: Record<string, any> = {
		'@context': 'https://schema.org',
		'@type': 'LocalBusiness',
		name: business.name,
		url: canonicalUrl,
	}

	// Description
	const description = business.aiDescription || business.description
	if (description) {
		schema.description = description
	}

	// Phone
	if (business.phone) {
		schema.telephone = business.phone
	}

	// Image
	if (business.featuredMedia?.url && business.featuredMedia.url) {
		schema.image = business.featuredMedia.url
	}

	// Address - best effort from business.address + first city
	if (business.address || business.cities.length > 0) {
		const addressSchema: Record<string, string> = {
			'@type': 'PostalAddress',
		}

		if (business.address) {
			addressSchema.streetAddress = business.address
		}

		if (business.cities.length > 0) {
			addressSchema.addressLocality = business.cities[0]!.name
			addressSchema.addressRegion = 'TX' // Dallas-Fort Worth area
			addressSchema.addressCountry = 'US'
		}

		schema.address = addressSchema
	}

	// Area served (all cities)
	if (business.cities.length > 0) {
		schema.areaServed = business.cities.map((city) => ({
			'@type': 'City',
			name: city.name,
		}))
	}

	// Knows about (categories/subcategories)
	const allCategories = [
		...business.categories.map((c) => c.name),
		...business.subcategories.map((s) => s.name),
	]
	if (allCategories.length > 0) {
		schema.knowsAbout = allCategories
	}

	// Same as (website)
	if (business.website) {
		schema.sameAs = [business.website]
	}

	return JSON.stringify(schema)
}

/**
 * Build WebApplication JSON-LD
 */
export function buildWebApplicationLd(params: {
	name: string
	url: string
	description?: string
}): string {
	const schema: Record<string, unknown> = {
		'@context': 'https://schema.org',
		'@type': 'WebApplication',
		name: params.name,
		url: params.url,
		applicationCategory: 'UtilitiesApplication',
		operatingSystem: 'All',
		offers: {
			'@type': 'Offer',
			price: '0',
			priceCurrency: 'USD',
		},
	}
	if (params.description) {
		schema.description = params.description
	}
	return JSON.stringify(schema)
}

/**
 * Build AggregateOffer JSON-LD
 */
export function buildAggregateOfferLd(params: {
	name: string
	lowPrice: number
	highPrice: number
	offerCount: number
	priceCurrency?: string
}): string {
	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'AggregateOffer',
		name: params.name,
		lowPrice: params.lowPrice.toFixed(2),
		highPrice: params.highPrice.toFixed(2),
		offerCount: params.offerCount,
		priceCurrency: params.priceCurrency ?? 'USD',
	})
}

/**
 * Build HowTo JSON-LD
 */
export function buildHowToLd(params: {
	name: string
	description?: string
	steps: Array<{ name: string; text: string }>
}): string {
	return JSON.stringify({
		'@context': 'https://schema.org',
		'@type': 'HowTo',
		name: params.name,
		...(params.description ? { description: params.description } : {}),
		step: params.steps.map((step, index) => ({
			'@type': 'HowToStep',
			position: index + 1,
			name: step.name,
			text: step.text,
		})),
	})
}

/**
 * Build FAQ JSON-LD from aiFaqSnippets
 * Generates standards-compliant FAQPage schema per https://schema.org/FAQPage
 */
export function buildFaqLd(faqItems: Array<{ q: string; a: string }>): string {
	const schema = {
		'@context': 'https://schema.org',
		'@type': 'FAQPage',
		mainEntity: faqItems.map((item) => ({
			'@type': 'Question',
			name: item.q,
			acceptedAnswer: {
				'@type': 'Answer',
				text: item.a,
			},
		})),
	}
	return JSON.stringify(schema)
}
